import type { FleetConfig } from '../config/FleetConfig';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';

/**
 * Tracks fleet center, direction, and composition.
 * Pure data object — updated by TravelSystem each frame.
 */
export class FleetState {
  centerX = 0;
  centerY = 0;
  centerZ = 0;
  dirX = 0;
  dirZ = -1;
  turnRate = 0;
  active = false;

  private _composition: number[];
  private _displayCounts: number[];
  private _cappedFlags: boolean[];
  private _warpCapacity: number[];

  constructor(config: FleetConfig) {
    this._composition = config.composition.slice();
    this._displayCounts = new Array(SHIP_TYPE_COUNT).fill(0);
    this._cappedFlags = new Array(SHIP_TYPE_COUNT).fill(false);
    this._warpCapacity = new Array(SHIP_TYPE_COUNT).fill(0);
    this._recomputeDisplay();
  }

  get composition(): readonly number[] { return this._composition; }

  setComposition(comp: number[]): void {
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      this._composition[i] = comp[i] ?? 0;
    }
    this._recomputeDisplay();
  }

  getDisplayCount(typeIdx: number): number { return this._displayCounts[typeIdx]; }
  isCapped(typeIdx: number): boolean { return this._cappedFlags[typeIdx]; }

  get totalShips(): number {
    let sum = 0;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) sum += this._composition[i];
    return sum;
  }

  get totalDisplayed(): number {
    let sum = 0;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) sum += this._displayCounts[i];
    return sum;
  }

  update(wx: number, wy: number, wz: number, dx: number, dz: number, turn = 0): void {
    this.centerX = wx;
    this.centerY = wy;
    this.centerZ = wz;
    this.dirX = dx;
    this.dirZ = dz;
    this.turnRate = turn;
  }

  canFleetWarp(): boolean {
    if (this.totalShips === 0) return false;
    const cap = this._warpCapacity;
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) cap[i] = 0;
    for (let c = 0; c < SHIP_TYPE_COUNT; c++) {
      const carry = SHIP_TYPE_DEFS[c].warpCarry;
      if (!carry || this._composition[c] === 0) continue;
      const count = this._composition[c];
      for (let t = 0; t < SHIP_TYPE_COUNT; t++) cap[t] += count * carry[t];
    }
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      if (this._composition[i] === 0) continue;
      if (SHIP_TYPE_DEFS[i].warpCapable) continue;
      if (this._composition[i] <= cap[i]) continue;
      return false;
    }
    return true;
  }

  private _recomputeDisplay(): void {
    for (let i = 0; i < SHIP_TYPE_COUNT; i++) {
      const actual = this._composition[i];
      const max = SHIP_TYPE_DEFS[i].displayMax;
      this._displayCounts[i] = Math.min(actual, max);
      this._cappedFlags[i] = actual > max;
    }
  }
}
